import React, { useContext, useState, useEffect } from "react";
import { UserContext } from "../../../context/UserContext";
import {
  Input,
  Button,
  Row,
  Col,
  Typography,
  Switch,
  Divider,
  Tag,
  notification,
} from "antd";
import { useLandlordSetup } from "../../../hooks/landlord/property/useLandlordSetup";
import { openError, openSuccess } from "../../../utils/helper/message";

const { Title, Text } = Typography;

const SetupPage = () => {
  const { user, setUser } = useContext(UserContext);
  const { updateLandlord } = useLandlordSetup();
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const isPhoneValid = (phone) => {
    if (!phone) return false;

    const trimmed = phone.trim();

    if (/[^+\d\s]/.test(trimmed)) return false;

    if (!trimmed.startsWith("+")) return false;

    const digitsOnly = trimmed.replace(/\D/g, "");
    if (digitsOnly.length < 10 || digitsOnly.length > 15) return false;

    return true;
  };

  const [formData, setFormData] = useState({
    fullName: `${user?.firstname || ""} ${user?.lastname || ""}`.trim(),
    companyName: user?.companyName || "",
    address: user?.address || "",
    apt: user?.apt || "",
    phone: user?.phone || "",
    emergencyPhone: user?.emergencyPhone || "",
    email: user?.email || "",
    taxId: user?.taxId || "",
  });

  const [preferences, setPreferences] = useState({
    rentReminders: true,
    helpfulTips: true,
    pendingReminders: true,
    taskNotifications: true,
    dailyApplicationEmails: true,
  });

  const togglePreference = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    if (!isPhoneValid(formData.phone)) {
      return openError(
        api,
        "Display phone must include country code (e.g. +1234567890)"
      );
    }

    if (formData.emergencyPhone && !isPhoneValid(formData.emergencyPhone)) {
      return openError(api, "Emergency phone must include country code");
    }

    if (formData.taxId && !/^\d{9}$/.test(formData.taxId)) {
      return openError(api, "Tax ID must be exactly 9 digits");
    }

    try {
      setLoading(true);
      const updated = await updateLandlord({
        ...formData,
        preferences,
      });
      setUser(updated);
      openSuccess(api, "Profile updated!");
    } catch (err) {
      console.error(err);
      openError(api, "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "900px" }}>
      {contextHolder}
      <Row gutter={[16, 16]}>
        {/* First Name */}
        <Col span={24}>
          <Title level={4}>Property Information</Title>
          <Title level={5}>
            Display Full Name <Text type="danger">*</Text>
          </Title>
          <Input
            value={formData.fullName}
            onChange={(e) => handleChange("fullName", e.target.value)}
          />
        </Col>

        {/* Company Name */}
        <Col span={24}>
          <Title level={5}>Company Name</Title>
          <Input
            value={formData.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
          />
        </Col>

        {/* Address + Apt */}
        <Col span={16}>
          <Title level={5}>Home or Office Address</Title>
          <Input
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </Col>
        <Col span={8}>
          <Title level={5}>Apt or Unit #</Title>
          <Input
            value={formData.apt}
            onChange={(e) => handleChange("apt", e.target.value)}
            type="number"
          />
        </Col>

        {/* Display + Emergency Phone */}
        <Col span={12}>
          <Title level={5}>Display Phone</Title>
          <Input
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            disabled
          />
        </Col>
        <Col span={12}>
          <Title level={5}>Emergency Contact Phone</Title>
          <Input
            value={formData.emergencyPhone}
            onChange={(e) => handleChange("emergencyPhone", e.target.value)}
          />
        </Col>

        {/* Tax Info */}
        <Divider />
        <Col span={12}>
          <Title level={4}>Tax Information</Title>
          <Text type="secondary">Enter your Tax ID (9 digits).</Text>
          <Input
            style={{ marginTop: 8 }}
            placeholder="123456789"
            maxLength={9}
            value={formData.taxId}
            onChange={(e) => handleChange("taxId", e.target.value)}
          />
        </Col>

        {/* Update Info Button */}
        <Col span={24} style={{ textAlign: "right" }}>
          <Button type="primary" onClick={handleUpdate}>
            Update Info
          </Button>
        </Col>

        {/* System Preferences */}
        <Divider />
        <Col span={24}>
          <Title level={4}>System Preferences</Title>
          <Row justify="space-between" align="middle">
            <Col>
              <Text>Send rent reminder emails to tenants</Text>
              <Text type="secondary" style={{ display: "block" }}>
                System will send emails to tenants 5 days before rent is due, on
                the due date, and 5 days after due
              </Text>
            </Col>
            <Col>
              <Switch
                checked={preferences.rentReminders}
                onChange={() => togglePreference("rentReminders")}
              />
            </Col>
          </Row>
        </Col>

        {/* Email Preferences */}
        <Divider />
        <Col span={24}>
          <Title level={4}>Email Preferences</Title>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col>
              <Text>
                Get helpful tips, secrets and how-to’s about your Avail account
              </Text>
              <Text type="secondary" style={{ display: "block" }}>
                How to Write a Rental Listing, 5 Steps To Screening Tenants,
                What to Do If Rent is Late, etc.
              </Text>
            </Col>
            <Col>
              <Switch
                checked={preferences.helpfulTips}
                onChange={() => togglePreference("helpfulTips")}
              />
            </Col>
          </Row>

          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col>
              <Text>
                Get reminders for pending actions and items your tenants are
                waiting on
              </Text>
              <Text type="secondary" style={{ display: "block" }}>
                Rent is overdue, Application is pending, Lease is expiring soon,
                Maintenance request incomplete, etc.
              </Text>
            </Col>
            <Col>
              <Switch
                checked={preferences.pendingReminders}
                onChange={() => togglePreference("pendingReminders")}
              />
            </Col>
          </Row>

          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col>
              <Text>
                Get notification emails about your property or when your tenants
                complete tasks
              </Text>
              <Text type="secondary" style={{ display: "block" }}>
                Rent was paid, Application completed, Lease has been signed,
                etc.
              </Text>
            </Col>
            <Col>
              <Switch defaultChecked />
            </Col>
          </Row>

          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col>
              <Text>Get daily notification emails about pending tasks</Text>
              <Text type="secondary" style={{ display: "block" }}>
                A single email each day with status updates about your active
                and pending rental applications.
              </Text>
            </Col>
            <Col>
              <Switch
                checked={preferences.taskNotifications}
                onChange={() => togglePreference("taskNotifications")}
              />
            </Col>
          </Row>

          <Row justify="space-between" align="middle">
            <Col>
              <Text>
                Get daily notification emails about your pending rental
                applications
              </Text>
              <Text type="secondary" style={{ display: "block" }}>
                A single email each day with status updates about your active
                and pending rental applications.
              </Text>
            </Col>
            <Col>
              <Switch
                checked={preferences.dailyApplicationEmails}
                onChange={() => togglePreference("dailyApplicationEmails")}
              />
            </Col>
          </Row>
        </Col>

        {/* Additional Settings */}
        <Divider />
        <Col span={24}>
          <Title level={4}>Additional Settings</Title>
          <Text type="secondary">
            Optionally “CC” a contractor on maintenance notifications and
            activity. You can enter a comma-separated list of emails.
          </Text>
          <Input placeholder="Enter email(s)" style={{ marginTop: 8 }} />
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Button>Update</Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SetupPage;
